import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import {
  DialogPresets,
  InputSearchFilter,
  TypedFormControl,
  toSearchStream,
} from '@principle-theorem/ng-shared';
import { Brand, Contact } from '@principle-theorem/principle-core';
import {
  IContact,
  IContactDetails,
  IContactFormData,
  ITag,
  isContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  addDoc,
  filterUndefined,
  isSameRef,
  multiSort,
  snapshotDefined,
} from '@principle-theorem/shared';
import { isEqual, isString } from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, skip, switchMap } from 'rxjs/operators';
import { OrganisationService } from '../../organisation.service';
import { PipesModule } from '../../pipes/pipes.module';
import { GlobalStoreService, TagType } from '../../store/global-store.service';
import { ContactFormDialogComponent } from '../contact-form-dialog/contact-form-dialog.component';
import { TagFilterComponent } from '../tag-filter/tag-filter.component';

@Component({
  selector: 'pr-contact-selector',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatIconModule,
    RouterModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatChipsModule,
    PipesModule,
    MatBadgeModule,
    TagFilterComponent,
  ],
  templateUrl: './contact-selector.component.html',
  styleUrl: './contact-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactSelectorComponent {
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _organisation = inject(OrganisationService);
  private _global = inject(GlobalStoreService);

  contact = input<WithRef<IContact> | undefined>(undefined);
  selectedContact = signal<WithRef<IContact> | undefined>(undefined, {
    equal: isEqual,
  });
  disabled = input(false, { transform: coerceBooleanProperty });
  compact = input(false, { transform: coerceBooleanProperty });
  label = input<string>('Contact');
  appearance = input<MatFormFieldAppearance>('outline');
  contactChanged = output<WithRef<IContact> | undefined>();
  selectedTags = signal<WithRef<ITag>[]>([]);
  selectedTagRefs = computed(() => this.selectedTags().map((tag) => tag.ref));

  contacts$: Observable<WithRef<IContact>[]>;
  availableTags$: Observable<WithRef<ITag>[]>;
  search: InputSearchFilter<WithRef<IContact>>;
  searchCtrl = new TypedFormControl<string>('');

  constructor() {
    this.contacts$ = this._organisation.brand$.pipe(
      filterUndefined(),
      switchMap((brand) => Brand.contacts$(brand)),
      multiSort()
    );

    this.availableTags$ = this._global.getTagsByType$(TagType.Contact);

    const filteredSource$ = combineLatest([
      this.contacts$,
      toObservable(this.selectedTags),
    ]).pipe(
      map(([contacts, tags]) => {
        if (!tags.length) {
          return contacts;
        }

        return contacts.filter(
          (contact) =>
            contact.tags &&
            tags.every((tag) =>
              contact.tags.some((current) => isSameRef(current, tag))
            )
        );
      })
    );

    this.search = new InputSearchFilter<WithRef<IContact>>(
      filteredSource$,
      toSearchStream(this.searchCtrl),
      ['name']
    );

    toObservable(this.contact)
      .pipe(
        filterUndefined(),
        filter((contact) => isContact(contact)),
        takeUntilDestroyed()
      )
      .subscribe((contact) => this.selectedContact.set(contact));

    toObservable(this.selectedContact)
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe((contact) => {
        this.searchCtrl.setValue(contact?.name || '', { emitEvent: false });
        this.contactChanged.emit(contact);
      });

    this.searchCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((searchValue) => {
        if (!searchValue && this.selectedContact()) {
          this.selectedContact.set(undefined);
        }
      });

    toObservable(this.disabled)
      .pipe(takeUntilDestroyed())
      .subscribe((disabled) =>
        disabled
          ? this.searchCtrl.disable({ emitEvent: false })
          : this.searchCtrl.enable({ emitEvent: false })
      );
  }

  clear(): void {
    this.searchCtrl.setValue('');
    this.selectedContact.set(undefined);
  }

  displayFn(value: WithRef<IContact> | string): string {
    if (!value) {
      return '';
    }
    if (isString(value)) {
      return value;
    }
    return value.name;
  }

  optionSelected(contact: WithRef<IContact> | undefined): void {
    this.selectedContact.set(contact);
  }

  tagsChanged(tags: WithRef<ITag>[]): void {
    this.selectedTags.set(tags);
  }

  async manageContact(): Promise<void> {
    const contact = this.selectedContact();
    const dialogData = {
      title: contact ? `Edit ${contact.name}` : 'Create Contact',
      submitLabel: contact ? 'Save' : 'Create',
      contact,
    };

    const config = DialogPresets.medium({
      data: dialogData,
      autoFocus: true,
    });

    const result = await this._dialog
      .open<
        ContactFormDialogComponent,
        IContactFormData,
        Partial<IContactDetails>
      >(ContactFormDialogComponent, config)
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }

    if (isContact(contact)) {
      await Firestore.patchDoc(contact.ref, result);
      this._snackBar.open('Contact Updated');
      this.selectedContact.set({ ...contact, ...result });
      return;
    }

    const newContact = await this._createNewContact(result);
    this.selectedContact.set(newContact);
    this._snackBar.open('Contact Created');
  }

  private async _createNewContact(
    data: Partial<IContactDetails>
  ): Promise<WithRef<IContact>> {
    const brand = await snapshotDefined(this._organisation.brand$);
    const collection = Brand.contactCol(brand);
    const docRef = await addDoc(collection, Contact.init(data));
    return Firestore.getDoc(docRef);
  }
}
